/** One page per person: what they did, what they approved, what is on them now.
 *
 *  Reports asks "how did the firm do". This asks "how is each person doing, and is the
 *  work spread fairly" — a different question, and the reason it is not a seventh
 *  section on a page that already has six. The figures are the same ones Reports
 *  computes, from the same request, so the two screens cannot disagree.
 *
 *  **The balance panel is a suggestion, not a score.** A load counts rows, and one
 *  Urgent row might be twenty minutes or two days. So every verdict shows the
 *  arithmetic that produced it and every person links to their actual tasks — the
 *  screen is built to be checked rather than believed.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Empty, ErrorNote, Spinner, monthStartISO, todayISO } from '@/components/ui'

type Person = {
  user_id: string; name: string
  assigned: number; completed: number; rate: number
  open_in_range: number; rework: number; carried: number
  decided: number; approved: number; rejected: number; rejection_rate: number
  median_wait_hours: number
  open_now: number; urgent_now: number; carried_now: number
  waiting_on_them: number; oldest_wait: number
  load: number; median_load: number
  signal: string; why: string
}
type Team = {
  range: { from: string; to: string }
  as_of: string
  median_load: number
  people: Person[]
}

// The firm's month, not the browser's - see `monthStartISO` in `ui`.
const monthStart = () => monthStartISO()

// How many names the "where to look" sentence spells out before counting the rest.
const NAMES_IN_SENTENCE = 3

/** The order the groups are read in. `overloaded` first because it is what the screen
 *  is opened for; `balanced` is the largest group at twenty people and the one nobody
 *  needs to read, so it starts collapsed. */
const GROUPS: { key: string; label: string; open: boolean }[] = [
  { key: 'overloaded', label: 'Carrying a lot', open: true },
  { key: 'capacity', label: 'Has room', open: true },
  { key: 'free', label: 'Nothing on them', open: true },
  { key: 'balanced', label: 'Balanced', open: false },
]

const TONE: Record<string, string> = {
  overloaded: 'bg-rej-tint text-rej-ink',
  capacity: 'bg-done-tint text-done-ink',
  free: 'bg-rule-2 text-ink-3',
  balanced: '',
}
const WORD: Record<string, string> = {
  overloaded: 'Carrying a lot',
  capacity: 'Has room',
  free: 'Nothing open',
  balanced: '',
}

function Stat({ label, value, hint }: {
  label: string; value: string | number; hint?: string
}) {
  return (
    <div className="min-w-0">
      <div className="text-[10.5px] text-ink-3 uppercase tracking-wide">{label}</div>
      <div className="mono text-[15px] font-semibold tracking-tight">{value}</div>
      {hint ? <div className="text-[10.5px] text-ink-3">{hint}</div> : null}
    </div>
  )
}

export function Team() {
  const [from, setFrom] = useState(monthStart())
  const [to, setTo] = useState(todayISO())
  const [find, setFind] = useState('')
  const [shut, setShut] = useState<Set<string>>(
    new Set(GROUPS.filter((g) => !g.open).map((g) => g.key)))

  const q = useQuery({
    queryKey: ['team', from, to],
    queryFn: () => api.get<Team>(`/reports/people?date_from=${from}&date_to=${to}`),
  })

  if (q.isLoading) return <Spinner label="Working out who is carrying what" />
  if (q.error) return <ErrorNote error={q.error} />
  const d = q.data!

  const flagged = d.people.filter((p) => p.signal === 'overloaded')
  const spare = d.people.filter((p) => p.signal === 'capacity' || p.signal === 'free')

  /** Three names and a count. At twenty people the old version joined every flagged
   *  name into one sentence, which stops being a sentence somewhere around five. */
  // One box rather than scrolling ten rows of cards to find a person. Matched before
  // grouping, so a search narrows every group at once and the counts stay honest.
  const matched = d.people.filter(
    (p) => p.name.toLowerCase().includes(find.trim().toLowerCase()))

  const names = (list: Person[]) => {
    const shown = list.slice(0, NAMES_IN_SENTENCE).map((p) => p.name).join(', ')
    const rest = list.length - NAMES_IN_SENTENCE
    return rest > 0 ? `${shown} and ${rest} others` : shown
  }

  return (
    <>
      <div className="card mb-4">
        <div className="card-head">
          <b className="text-[13px]">Everyone</b>
          <span className="text-[11.8px] text-ink-3">
            Performance over the dates you pick; workload as it stands today
          </span>
          <span className="flex-1" />
          <input type="date" className="field w-auto text-[12px]" value={from}
                 onChange={(e) => setFrom(e.target.value)} />
          <input type="date" className="field w-auto text-[12px]" value={to}
                 onChange={(e) => setTo(e.target.value)} />
          <input className="field w-auto text-[12px]" value={find} placeholder="Find"
                 onChange={(e) => setFind(e.target.value)} />
        </div>

        {(flagged.length > 0 || spare.length > 0) && (
          <div className="px-4 py-3 border-b border-rule">
            <b className="text-[12.5px]">Where to look</b>
            <p className="mt-1 text-[12px] text-ink-2 leading-relaxed">
              {flagged.length > 0 && (
                <>
                  <b>{names(flagged)}</b>{' '}
                  {flagged.length === 1 ? 'is' : 'are'} carrying more than the rest
                  {spare.length > 0 && (
                    <>; <b>{names(spare)}</b>{' '}
                    {spare.length === 1 ? 'has' : 'have'} room</>
                  )}.
                </>
              )}
              {flagged.length === 0 && spare.length > 0 && (
                <>Nobody is obviously overloaded.{' '}
                <b>{names(spare)}</b>{' '}
                {spare.length === 1 ? 'has' : 'have'} room if something needs
                moving.</>
              )}
            </p>
            <p className="mt-1.5 text-[11px] text-ink-3 leading-snug">
              Worked out from open tasks weighted by priority, against a median of{' '}
              <b className="mono">{d.median_load}</b>. It counts rows, not hours — one
              urgent task might be twenty minutes or two days, so treat this as where
              to look rather than what to conclude.
            </p>
          </div>
        )}
      </div>

      {d.people.length === 0 ? (
        <Empty title="Nobody to report on"
               hint="No tasks in this range, and nothing open right now." />
      ) : matched.length === 0 ? (
        <Empty title="Nobody by that name"
               hint="Clear the search to see everyone again."
               action={<button className="btn btn-primary"
                               onClick={() => setFind('')}>Clear</button>} />
      ) : (
        GROUPS.map((g) => {
          const people = matched.filter((p) => p.signal === g.key)
          if (people.length === 0) return null
          const closed = shut.has(g.key)
          return (
            <div key={g.key} className="mb-4">
              <button type="button"
                      className="flex items-baseline gap-2 mb-2 w-full text-left"
                      onClick={() => setShut((prev) => {
                        const next = new Set(prev)
                        if (closed) next.delete(g.key)
                        else next.add(g.key)
                        return next
                      })}>
                <b className="text-[12.5px]">{g.label}</b>
                <span className="mono text-[11.5px] text-ink-3">{people.length}</span>
                <span className="text-[11px] text-ink-3">
                  {closed ? 'show' : 'hide'}
                </span>
              </button>
              {!closed && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {people.map((p) => (
            <div key={p.user_id} className="card p-4">
              <div className="flex items-baseline gap-2 mb-3">
                <Link to={`/tasks?${new URLSearchParams({
                        range: 'custom', from, to, responsible: p.user_id })}`}
                      className="text-[14px] font-semibold tracking-tight
                                 text-primary hover:underline">
                  {p.name}
                </Link>
                {p.signal !== 'balanced' && (
                  <span className={`text-[10.5px] px-1.5 py-0.5 rounded
                                    ${TONE[p.signal] ?? ''}`}>
                    {WORD[p.signal]}
                  </span>
                )}
                <span className="flex-1" />
                {p.why ? (
                  <span className="text-[10.5px] text-ink-3">{p.why}</span>
                ) : null}
              </div>

              {/* Hidden for somebody who only approves: four zeros about work that was
                  never theirs reads as a judgement, and is not one. */}
              {(p.assigned > 0 || p.open_now > 0) && (
              <div className="grid grid-cols-4 gap-3 pb-3 border-b border-rule">
                <Stat label="Assigned" value={p.assigned} />
                <Stat label="Finished" value={`${p.rate}%`}
                      hint={`${p.completed} of ${p.assigned}`} />
                <Stat label="Sent back" value={p.rework}
                      hint={p.rework ? 'rejected at least once' : ''} />
                <Stat label="Carried" value={p.carried}
                      hint={p.carried ? 'moves, all tasks' : ''} />
              </div>
              )}

              {(p.decided > 0 || p.waiting_on_them > 0) && (
                <div className="grid grid-cols-4 gap-3 py-3 border-b border-rule">
                  <Stat label="Decided" value={p.decided} hint="as approver" />
                  <Stat label="Rejected" value={`${p.rejection_rate}%`}
                        hint={`${p.rejected} of ${p.decided}`} />
                  <Stat label="Typical wait"
                        value={p.median_wait_hours ? `${p.median_wait_hours}h` : '—'}
                        hint="median, to decide" />
                  <Stat label="Waiting now" value={p.waiting_on_them}
                        hint={p.oldest_wait
                          ? `oldest ${p.oldest_wait}d` : ''} />
                </div>
              )}

              <div className="grid grid-cols-4 gap-3 pt-3">
                <Stat label="Open now" value={p.open_now} />
                <Stat label="Urgent" value={p.urgent_now} />
                <Stat label="Carried" value={p.carried_now} hint="of those open" />
                <Stat label="Load" value={p.load}
                      hint={`median ${p.median_load}`} />
              </div>
            </div>
          ))}
        </div>
              )}
            </div>
          )
        })
      )}

      <p className="mt-3 text-[11.5px] text-ink-3">
        The first two rows cover {d.range.from} to {d.range.to}. The last row is right
        now, {d.as_of} — change the dates and it will not move, which is correct.
      </p>
    </>
  )
}
